"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Clock,
  Check,
  TriangleAlert,
  Sparkles,
  Wand2,
  Loader2,
} from "lucide-react";
import { apiClient, isPaidRequiredError, type AlertDoc } from "@/lib/api-client";
import { ICY, RADIUS } from "@/lib/alert-theme";
import {
  IcyDialogShell,
  PrimaryDialogButton,
  SecondaryDialogButton,
  DIALOG_LABEL_CLASS,
  DIALOG_ERROR_CLASS,
} from "./IcyDialogShell";

/**
 * EditScheduleDialog — compact preset grid + plain-English parser.
 *
 * Plain-English input behavior:
 *   - Parse is triggered ONLY by an explicit user action: pressing Enter in
 *     the input, or clicking the wand button. We do NOT parse on every
 *     keystroke. (Previously an 800ms idle debounce auto-parsed and then
 *     REPLACED the user's text mid-sentence with Gemini's description — users
 *     reported it "changed their words without waiting". Manual trigger fixes
 *     that: you type the whole sentence, then fire the parse when ready.)
 *   - Skipped when text is shorter than 6 chars.
 *   - Client-side cache keyed on trimmed text — retyping the same phrase
 *     never re-hits Gemini. Keeps latency + cost low.
 *   - On a successful parse we NEVER overwrite the input. The parsed result
 *     (human description + raw cron chip + confidence) is shown as a small
 *     confirmation row BELOW the input, so the text you typed stays intact.
 *   - Server still re-validates with croniter + 60s minimum regardless of
 *     what Gemini says, so even a bad parse can't schedule something unsafe.
 */

type PresetId =
  | "every-5-min-market"
  | "every-15-min-market"
  | "every-30-min-market"
  | "hourly-market"
  | "market-open"
  | "market-close"
  | "daily-pre-market"
  | "weekly-monday";

type PresetOption = {
  id: PresetId;
  label: string;
  sublabel: string;
  cron: string;
  description: string;
};

const PRESETS: PresetOption[] = [
  {
    id: "every-5-min-market",
    label: "Every 5 min",
    sublabel: "Market hours",
    cron: "*/5 9-16 * * 1-5",
    description: "Every 5 minutes, 9am–4pm ET, weekdays",
  },
  {
    id: "every-15-min-market",
    label: "Every 15 min",
    sublabel: "Market hours",
    cron: "*/15 9-16 * * 1-5",
    description: "Every 15 minutes, 9am–4pm ET, weekdays",
  },
  {
    id: "every-30-min-market",
    label: "Every 30 min",
    sublabel: "Market hours",
    cron: "*/30 9-16 * * 1-5",
    description: "Every 30 minutes, 9am–4pm ET, weekdays",
  },
  {
    id: "hourly-market",
    label: "Hourly",
    sublabel: "Market hours",
    cron: "0 9-16 * * 1-5",
    description: "Every hour on the hour, 9am–4pm ET, weekdays",
  },
  {
    id: "market-open",
    label: "Market open",
    sublabel: "9:30 AM ET",
    cron: "30 9 * * 1-5",
    description: "Every weekday at market open (9:30 AM ET)",
  },
  {
    id: "market-close",
    label: "Market close",
    sublabel: "4:00 PM ET",
    cron: "0 16 * * 1-5",
    description: "Every weekday at market close (4:00 PM ET)",
  },
  {
    id: "daily-pre-market",
    label: "Pre-market",
    sublabel: "8:00 AM ET",
    cron: "0 8 * * 1-5",
    description: "Every weekday at 8:00 AM ET (pre-market)",
  },
  {
    id: "weekly-monday",
    label: "Weekly",
    sublabel: "Mon 9:00 AM ET",
    cron: "0 9 * * 1",
    description: "Every Monday at 9:00 AM ET",
  },
];

// Minimum characters before a manual parse is allowed (guards against firing
// on "ev" / "eve"). Parse is manual-trigger only now (Enter / wand click), so
// there's no debounce timer anymore.
const PARSE_MIN_CHARS = 6;

type ParseState = {
  ok: boolean;
  cron: string | null;
  schedule_description: string | null;
  confidence: "high" | "medium" | "low" | null;
  interval_seconds: number | null;
  error: string | null;
};

const CONFIDENCE_STYLE: Record<"high" | "medium" | "low", string> = {
  high: "bg-emerald-400/15 text-emerald-200 border-emerald-300/40",
  medium: "bg-amber-400/15 text-amber-200 border-amber-300/40",
  low: "bg-orange-400/15 text-orange-200 border-orange-300/40",
};

const CONFIDENCE_LABEL: Record<"high" | "medium" | "low", string> = {
  high: "High confidence",
  medium: "Double-check",
  low: "Low confidence",
};

export function EditScheduleDialog({
  open,
  onOpenChange,
  alert,
  canMutate = true,
  onSaved,
  onPaidRequired,
  viewMode,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  alert: AlertDoc;
  /** When false, the dialog still opens (so free users can preview the
   *  flow), but Save short-circuits to the paywall instead of hitting
   *  the API. Defaults to true to preserve owner/paid-user behavior. */
  canMutate?: boolean;
  onSaved: () => void;
  onPaidRequired?: () => void;
  viewMode?: string;
}) {
  const currentCron = alert.schedule || "";
  const matchedPreset = useMemo(
    () => PRESETS.find((p) => p.cron === currentCron) || null,
    [currentCron],
  );

  const [selectedPresetId, setSelectedPresetId] = useState<PresetId | null>(
    matchedPreset?.id ?? null,
  );

  // The plain-English text the user typed. We NEVER overwrite this with the
  // parsed description — the parse result is shown separately below.
  const [nlText, setNlText] = useState("");

  const [parsing, setParsing] = useState(false);
  const [parseResult, setParseResult] = useState<ParseState | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Client-side parse cache. Keys are normalized (trimmed + lowercased)
  // query strings, so "every 5 min" and " Every 5 MIN " share a cache hit.
  const parseCacheRef = useRef<Map<string, ParseState>>(new Map());

  useEffect(() => {
    if (open) {
      setSelectedPresetId(matchedPreset?.id ?? null);
      setNlText("");
      setParseResult(null);
      setParsing(false);
      setSaveError(null);
      parseCacheRef.current = new Map();
    }
  }, [open, matchedPreset]);

  // Which cron will actually save — preset wins if selected, else the parsed
  // result. Never uses raw un-parsed text for safety.
  const effectiveCron: string | null = selectedPresetId
    ? (PRESETS.find((p) => p.id === selectedPresetId)?.cron ?? null)
    : parseResult?.ok
      ? parseResult.cron
      : null;

  const effectiveDescription: string | undefined = selectedPresetId
    ? PRESETS.find((p) => p.id === selectedPresetId)?.description
    : parseResult?.ok
      ? parseResult.schedule_description || undefined
      : undefined;

  const canSave =
    !!effectiveCron && effectiveCron !== currentCron && !saving && !parsing;

  const runParse = async (text: string) => {
    const normalized = text.trim().toLowerCase();
    if (!normalized || normalized.length < PARSE_MIN_CHARS) {
      setParseResult(null);
      setParsing(false);
      return;
    }

    // Cache hit → reuse immediately, no network. (Text is left as the user
    // typed it; the parsed result renders in the confirmation row below.)
    const cached = parseCacheRef.current.get(normalized);
    if (cached) {
      setParseResult(cached);
      setParsing(false);
      return;
    }

    setParsing(true);
    setSaveError(null);
    try {
      const res = (await apiClient.parseAlertSchedule({
        text: text.trim(),
      })) as ParseState;
      parseCacheRef.current.set(normalized, res);
      setParseResult(res);
    } catch (err: any) {
      const msg =
        err?.message?.replace(/^Failed to POST [^:]+:\s*\d+\s*/, "") ||
        "Couldn't reach the parser.";
      const failState: ParseState = {
        ok: false,
        cron: null,
        schedule_description: null,
        confidence: null,
        interval_seconds: null,
        error: msg,
      };
      setParseResult(failState);
    } finally {
      setParsing(false);
    }
  };

  const handleNlChange = (text: string) => {
    // Pure typing handler: update the text, deselect any preset, and clear a
    // stale parse result so the confirmation row doesn't show a cron that no
    // longer matches what's in the box. NO parsing happens here — that's
    // manual-trigger only (Enter / wand), so we never overwrite the user.
    setNlText(text);
    setSelectedPresetId(null);
    if (parseResult) setParseResult(null);
  };

  // Whether a manual parse is currently allowed (enables the wand + Enter).
  const canParse = nlText.trim().length >= PARSE_MIN_CHARS && !parsing;

  // Explicit parse trigger (Enter key or wand-button click).
  const triggerParse = () => {
    if (!canParse) return;
    runParse(nlText);
  };

  const handleSave = async () => {
    if (!canSave || !effectiveCron) return;
    // Free-tier client-side gate. Lets non-paying users explore the
    // dialog (preset grid + plain-English parse), but the moment they
    // try to actually persist a schedule, we route through the paywall
    // instead of burning a backend call that would 402 anyway.
    if (!canMutate) {
      onOpenChange(false);
      onPaidRequired?.();
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      await apiClient.updateAlertSchedule(alert._id, {
        schedule: effectiveCron,
        schedule_description: effectiveDescription,
      });
      onSaved();
      onOpenChange(false);
    } catch (err: any) {
      if (isPaidRequiredError(err)) {
        onOpenChange(false);
        onPaidRequired?.();
        return;
      }
      const msg = err?.message || "Could not update schedule";
      setSaveError(msg.replace(/^Failed to PATCH [^:]+:\s*\d+\s*/, ""));
    } finally {
      setSaving(false);
    }
  };

  return (
    <IcyDialogShell
      open={open}
      onOpenChange={onOpenChange}
      title="Change schedule"
      subtitle="Pick a preset or describe it in plain English"
      Icon={Clock}
      size="md"
      contentTestId="schedule-dialog"
      closeButtonTestId="schedule-dialog-close"
      footer={
        <>
          <SecondaryDialogButton
            onClick={() => onOpenChange(false)}
            disabled={saving}
          >
            Cancel
          </SecondaryDialogButton>
          <PrimaryDialogButton
            onClick={handleSave}
            busy={saving}
            disabled={!canSave}
            Icon={Check}
            variant={viewMode === "signal" ? "emerald" : "icy"}
          >
            Save schedule
          </PrimaryDialogButton>
        </>
      }
    >
      {/* Current schedule banner ------------------------------------------- */}
      <div
        className={`${RADIUS.chip} border ${ICY.ring} bg-slate-950/40 px-3 py-2 flex items-start sm:items-center gap-2`}
      >
        <Sparkles className={`w-3.5 h-3.5 shrink-0 ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`} />
        <div className="flex flex-col min-w-0 flex-1 gap-0.5">
          <span className={`text-[10px] uppercase tracking-wider font-semibold ${viewMode === "signal" ? "text-emerald-100/80" : "text-sky-100/80"}`}>
            Currently running
          </span>
          <span className="text-[11.5px] text-slate-100/90 truncate">
            {alert.schedule_description || alert.schedule || "—"}
          </span>
        </div>
        <code className="hidden sm:block text-[10px] font-mono text-slate-300/80 tabular-nums shrink-0">
          {currentCron}
        </code>
      </div>

      {/* Preset grid — tight 4×2 -------------------------------------------
          `data-testid="schedule-presets"` is the alert-tour anchor
          for the beat that walks the user through preset selection.
          Tagged on the wrapper grid so the spotlight wraps all 8
          presets cleanly with one ring. */}
      <div
        data-testid="schedule-presets"
        className="grid grid-cols-2 sm:grid-cols-4 gap-1.5"
      >
        {PRESETS.map((p) => {
          const active = selectedPresetId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => {
                setSelectedPresetId(p.id);
                setNlText("");
                setParseResult(null);
                setSaveError(null);
              }}
              className={[
              "text-left px-2 py-2 sm:py-1.5 rounded-md border transition-all",
                viewMode === "signal" ? "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-300/60" : "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/60",
                active
                  ? viewMode === "signal" ? "bg-emerald-200/10 border-emerald-300/55 shadow-[0_0_0_1px_rgba(110,231,183,0.25)_inset]" : "bg-sky-200/10 border-sky-300/55 shadow-[0_0_0_1px_rgba(186,230,253,0.25)_inset]"
                  : "bg-slate-950/40 border-slate-300/15 hover:border-slate-300/35 hover:bg-slate-900/60",
              ].join(" ")}
            >
              <div
                className={[
                  "text-[11.5px] font-semibold leading-tight",
                  active ? viewMode === "signal" ? "text-emerald-50" : "text-sky-50" : "text-slate-100",
                ].join(" ")}
              >
                {p.label}
              </div>
              <div className="text-[10px] text-slate-300/80 leading-tight mt-0.5">
                {p.sublabel}
              </div>
            </button>
          );
        })}
      </div>

      {/* Divider ----------------------------------------------------------- */}
      <div className="flex items-center gap-2 pt-0.5">
        <div className="h-px flex-1 bg-slate-300/10" />
        <span className="text-[10px] uppercase tracking-widest text-slate-400/80">
          or describe it
        </span>
        <div className="h-px flex-1 bg-slate-300/10" />
      </div>

      {/* Plain-English input — parsed description replaces the text -------
          `data-testid="schedule-nl"` is the alert-tour anchor for the
          beat that walks the user through the natural-language input.
          Tagged on the section wrapper (label + input + hint row) so
          the spotlight covers the whole feature, not just the bare
          text input. */}
      <div data-testid="schedule-nl">
        <label className={DIALOG_LABEL_CLASS} htmlFor="nl-schedule">
          Plain English
        </label>
        <div className="relative">
          <input
            id="nl-schedule"
            type="text"
            className={[
              "w-full bg-slate-950/60 border rounded-md px-3 py-2 pr-12 sm:pr-28",
              "text-[12.5px] text-slate-100 placeholder-slate-500",
              viewMode === "signal" ? "focus:outline-none focus:ring-2 focus:ring-emerald-300/60" : "focus:outline-none focus:ring-2 focus:ring-sky-300/60",
              parseResult?.ok === false
                ? "border-amber-300/40"
                : viewMode === "signal" ? "border-slate-300/15 focus:border-emerald-300/55" : "border-slate-300/15 focus:border-sky-300/55",
            ].join(" ")}
            placeholder="e.g. every 15 min during market hours, daily at 3pm ET"
            value={nlText}
            onChange={(e) => handleNlChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                // Parse on Enter; never submit the dialog from this field.
                e.preventDefault();
                triggerParse();
              }
            }}
            maxLength={500}
            autoComplete="off"
          />
          {/* Right-side action zone: a clickable wand that runs the parse.
              Parsing only ever happens from here or from Enter — never on
              keystroke — so the text the user typed is never overwritten. */}
          <button
            type="button"
            onClick={triggerParse}
            disabled={!canParse}
            title={
              canParse
                ? "Convert to a schedule (or press Enter)"
                : "Type at least a few words, then click to convert"
            }
            aria-label="Convert plain English to a schedule"
            className={[
              "absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center",
              "h-7 w-7 rounded-md transition-colors",
              canParse
                ? viewMode === "signal" ? "text-emerald-300 hover:bg-emerald-400/15 cursor-pointer" : "text-sky-300 hover:bg-sky-400/15 cursor-pointer"
                : "text-slate-500 cursor-not-allowed",
            ].join(" ")}
          >
            {parsing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Wand2 className="w-3.5 h-3.5" />
            )}
          </button>
        </div>
        <p className="text-[10.5px] text-slate-400/80 leading-relaxed mt-1">
          Press <span className="text-slate-200">Enter</span> or tap the wand to
          convert · Min 1 minute · Timezone{" "}
          <span className="text-slate-200">America/New_York</span>
        </p>

        {/* Parse confirmation row — shows what we understood WITHOUT touching
            the text the user typed. Appears only after a successful parse. */}
        {parseResult?.ok && parseResult.cron && (
          <div
            className={`${RADIUS.chip} mt-2 border ${ICY.ring} bg-slate-950/40 px-3 py-2 flex flex-wrap sm:flex-nowrap items-center gap-2`}
          >
            <Check className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
            <div className="flex flex-col min-w-0 flex-1 gap-0.5">
              <span className="text-[10px] uppercase tracking-wider text-emerald-100/80 font-semibold">
                Will save as
              </span>
              <span className="text-[11.5px] text-slate-100/90 truncate">
                {parseResult.schedule_description || parseResult.cron}
              </span>
            </div>
            {parseResult.confidence && (
              <span
                className={[
                  "text-[9.5px] uppercase tracking-wider font-semibold shrink-0",
                  "px-1.5 py-0.5 rounded border",
                  CONFIDENCE_STYLE[parseResult.confidence],
                ].join(" ")}
              >
                {CONFIDENCE_LABEL[parseResult.confidence]}
              </span>
            )}
            <code
              className="text-[10px] font-mono text-slate-300/80 tabular-nums shrink-0 truncate max-w-[96px] sm:max-w-[110px]"
              title={parseResult.cron}
            >
              {parseResult.cron}
            </code>
          </div>
        )}
      </div>

      {/* Inline parse-failure warning (keeps text the user typed) ---------- */}
      {parseResult && parseResult.ok === false && parseResult.error && (
        <div
          className={[
            "rounded-md border px-3 py-2 flex items-start gap-2",
            "bg-amber-400/5 border-amber-300/30",
          ].join(" ")}
        >
          <TriangleAlert className="w-3.5 h-3.5 text-amber-200 shrink-0 mt-0.5" />
          <span className="text-[11.5px] text-amber-100/95 leading-snug">
            {parseResult.error}
          </span>
        </div>
      )}

      {saveError && (
        <div className={DIALOG_ERROR_CLASS}>
          <TriangleAlert className="w-3.5 h-3.5" />
          <span>{saveError}</span>
        </div>
      )}
    </IcyDialogShell>
  );
}
